import {
  DestroyRef,
  Directive,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup } from "@angular/forms";
import { IEnrichmentModel } from "@app/shared/models/enrichment.model";
import { debounceTime, filter } from "rxjs";
import { EnrichmentDataService } from "../../../../services/enrichment-data.service";
import { SectionDirtyStore } from "../../enrichment-edit-package/section-dirty.store";
import { FormUpdateGuard } from "./form-update-guard";

/**
 * Base class for enrichment tabs.
 *
 * Standardises the three lifecycles every tab needs to implement today:
 *   1. Subscribe to `enrichment$`. When a new model arrives, call `hydrate()`
 *      so the tab repopulates its form from the model — wrapped in
 *      `FormUpdateGuard.run()` so `valueChanges` does not fire back into the
 *      data service.
 *   2. Subscribe to `form.valueChanges`. When the user edits, call
 *      `serialize()` to build the next model and push it via
 *      `EnrichmentDataService.setEnrichment`.
 *   3. Tear everything down on destroy via `DestroyRef + takeUntilDestroyed`.
 *
 * Concrete tabs:
 *   - declare `form` and implement `hydrate(model)` + `serialize(model, value)`
 *   - optionally override `commitDebounceMs` (default 0 = synchronous, matches
 *     what every tab today does — except `enrichment-tab-display` which uses
 *     150ms; that tab can override).
 *   - optionally override `onAfterInit()` for extra subscriptions (completion
 *     counts, child-form handling, etc.). It runs after the base wiring.
 *
 * Tabs that have not migrated yet keep their existing `Subject + takeUntil`
 * pattern — adopting the base class is opt-in.
 */
@Directive()
export abstract class EnrichmentTabBase<TForm extends FormGroup = FormGroup>
  implements OnInit, OnDestroy
{
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly data = inject(EnrichmentDataService);
  /** Section-dirty attribution — which TAB the discard snapshot should
   *  blame. Optional: absent when a tab is mounted standalone in specs (see
   *  `enrichment-tab.base.spec.ts`'s `TestTab` fixture, which provides none). */
  protected readonly sectionDirty = inject(SectionDirtyStore, {
    optional: true,
  });
  protected readonly updating = new FormUpdateGuard();

  /** Cached snapshot of the latest enrichment model emitted on `enrichment$`.
   *  Used as the input to `serialize()` on every commit, so subclasses do not
   *  need to read it back from the data service (which simplifies mocking). */
  private currentModel: IEnrichmentModel | null = null;

  abstract form: TForm;

  /** The section-dirty tab id this concrete tab's commits stamp (e.g.
   *  `"product-attributes"`). `null` (the default) opts a tab out of
   *  per-section dirty tracking — override in subclasses that participate. */
  protected readonly sectionTabId: string | null = null;

  /**
   * Called when a new enrichment model arrives. Implementations should patch
   * the form and rebuild any `FormArray`s. Already wrapped in `updating.run()`
   * — implementations do NOT need to set the guard themselves.
   */
  protected abstract hydrate(model: IEnrichmentModel): void;

  /**
   * Called when the user edits the form. Implementations should produce the
   * next enrichment model from the current model + the form value.
   */
  protected abstract serialize(
    model: IEnrichmentModel,
    value: TForm["value"],
  ): IEnrichmentModel;

  /** Default 0 = synchronous commit (matches every existing tab today, except
   *  enrichment-tab-display which can override to 150). */
  protected readonly commitDebounceMs: number = 0;

  /** Optional hook for subclasses to register extra subscriptions after base
   *  wiring is in place. Runs once at the end of `ngOnInit()`. */
  protected onAfterInit(): void {
    // override in subclass when needed
  }

  ngOnInit(): void {
    this.data.enrichment$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((model) => {
        if (model) {
          this.currentModel = model;
          this.updating.run(() => this.hydrate(model));
        }
      });

    const stream = this.form.valueChanges.pipe(
      filter(() => !this.updating.busy),
    );
    const piped =
      this.commitDebounceMs > 0
        ? stream.pipe(debounceTime(this.commitDebounceMs))
        : stream;
    piped
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.commit(value as TForm["value"]));

    this.onAfterInit();
  }

  /** No-op: `DestroyRef + takeUntilDestroyed` handles teardown. Implemented to
   *  satisfy the OnDestroy interface so subclasses can declare it without
   *  having to wire their own destroy$. Subclasses that need extra cleanup
   *  can `super.ngOnDestroy()` then add their own logic. */
  ngOnDestroy(): void {
    // no-op
  }

  private commit(value: TForm["value"]): void {
    if (!this.currentModel) return;
    // Always mirror the on-screen value into the model, even when a cross-field
    // validator (e.g. a min > max range) fails — the invalid value is surfaced
    // inline, but the model (and therefore the publish readiness view) must
    // stay in sync with what the user typed/cleared. Gating on `form.invalid`
    // here froze the whole tab's model whenever one range was off, so readiness
    // stopped reflecting later edits.
    const next = this.serialize(this.currentModel, value);
    // `serialize` typically mutates `__detail` IN PLACE and returns the same
    // model object. The shell reads readiness/preview through `toSignal`, which
    // DEDUPES same-reference emissions — so a same-ref commit would leave
    // readiness stale (e.g. Network Type filled but still flagged "must fix").
    // Emit a fresh top-level reference so those signals refire; nested data is
    // shared by reference, so this stays cheap.
    this.data.setEnrichment(next ? { ...next } : next);
    if (this.sectionTabId) this.sectionDirty?.markTabDirty(this.sectionTabId);
  }
}
